import { Component, ElementRef, Inject, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { BaseControl } from '../../../shared/toolbar/controls/base-control';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../../../shared/contracts/toolbar-control.tokens';
import { ToolbarDrawerService } from '../../../shared/toolbar/toolbar-drawer.service';
import { ApiService } from 'src/app/services/api.service';
import { PopupService } from 'src/app/services/popup.service';
import { CollectionPopupComponent } from 'src/app/components/shared';
import {
  VesselFinderMapController,
  VesselFinderState,
  VesselFinderVesselRow,
  vesselRowHasFix,
} from '../../vesselfinder-map.controller';
import { parseVesselIdentifiers } from '../../vessel-identifier.model';
import { actionCredits, searchActionKey } from 'src/app/services/operators/vesselfinder-credit-actions';
import {
  applyVesselFinderCreditSpend,
  vesselFinderSpendBlocker,
  vesselFinderSpendRefusal,
} from '../../vf-credits.model';

/**
 * VesselFinder's search control — SAME UI/UX shape as Voyage Radar's
 * `SearchControlComponent`/`SearchPanelComponent` (`sp-*` classes copied
 * verbatim, `tb-drawer__primary`/`tb-drawer__cost` reused from the shared
 * drawer's `::ng-deep` vocabulary): a single search box, a free "on the map"
 * list filtered live as you type, then one paid "Search worldwide" button.
 *
 * WHERE IT GENUINELY DIFFERS, because the underlying capability differs, not
 * the design: Voyage Radar's free list filters a drawn AOI snapshot by name;
 * VesselFinder draws no snapshot, so its free list is whatever this session
 * has already located (searched or saved), and its worldwide call resolves an
 * exact MMSI/IMO rather than text-matching a name (backend plan §1.2).
 *
 * Clicking ANY row — free or worldwide — centers the map on that hull and
 * opens its popup (`vf.centerVessel`), exactly like Voyage Radar's
 * `pickLocal`/`pickWorldwide`. The popup is where Master/Voyage/Port
 * calls/Watch now live (`vessel-popup.ts`) — this drawer no longer carries
 * per-row action buttons, matching Voyage Radar's own search drawer, which
 * never had them either.
 */
@Component({
  selector: 'app-vf-search-control',
  template: `
    <button #trigger type="button" class="tb-btn"
            [class.tb-btn--active]="drawer.isOpen(panel)"
            (click)="onToggleDrawer(trigger)"
            aria-label="Search vessels"
            [matTooltip]="'Search vessels · ' + subtitle()"
            matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">search</mat-icon>
    </button>

    <ng-template #panel>
      <ng-container *ngIf="vfCtrl.state$ | async as s">
        <app-toolbar-drawer title="Vessel search" [subtitle]="subtitle()" (close)="onCloseDrawer()">
        <div class="sp-panel">
          <div class="sp-box">
            <mat-icon class="material-icons-outlined sp-box__mag">search</mat-icon>
            <input #box
                   class="sp-box__input"
                   type="text"
                   placeholder="MMSI (9 digits) or IMO (7 digits)"
                   [(ngModel)]="raw"
                   (ngModelChange)="onQueryChange()"
                   (keydown.enter)="searchTerrestrial()"
                   aria-label="Search vessels" />
            <button type="button" class="sp-box__clear" *ngIf="raw" (click)="clear()" aria-label="Clear search">
              <mat-icon>close</mat-icon>
            </button>
          </div>
          <div class="sp-hint">Multiple MMSI or IMO can be searched using comma separation (,)</div>

          <div class="sp-actions">
            <button type="button" class="tb-drawer__primary vf-cta"
                    [disabled]="loading || !raw.trim() || !!terrestrialBlocker()"
                    (click)="searchTerrestrial()">
              <mat-icon class="material-icons-outlined" [class.sp-spin]="loading && !useSatellite">public</mat-icon>
              <span class="tb-drawer__primary-label">{{ (loading && !useSatellite) ? 'Searching Terrestrial…' : 'Terrestrial Search' }}</span>
            </button>
            <button type="button" class="tb-drawer__primary vf-cta"
                    [disabled]="loading || !raw.trim() || !!satelliteBlocker()"
                    (click)="searchSatellite()">
              <mat-icon class="material-icons-outlined" [class.sp-spin]="loading && useSatellite">satellite_alt</mat-icon>
              <span class="tb-drawer__primary-label">{{ (loading && useSatellite) ? 'Searching Satellite…' : 'Satellite Search' }}</span>
            </button>
          </div>
          <div class="tb-drawer__cost" *ngIf="terrestrialBlocker() as why">{{ why }}</div>

          <ng-container *ngIf="results.length">
            <div class="sp-list">
              <button type="button" class="sp-row" *ngFor="let r of results"
                      [class.sp-row--selected]="isSelected(r)"
                      (click)="pick(r, $event)">
                <span class="sp-row__gl">◆</span>
                <span class="sp-row__main">
                  <span class="sp-row__nm">{{ r.name || identifierLabel(r) }}</span>
                  <span class="sp-row__meta">{{ fullMeta(r) }}</span>
                </span>
                <span class="sp-tag sp-tag--kept" *ngIf="isKept(r, s)">KEPT</span>
              </button>
            </div>
          </ng-container>

          <ng-container *ngIf="!results.length && localHits(s).length">
            <div class="sp-list">
              <button type="button" class="sp-row" *ngFor="let hit of localHits(s)"
                      [class.sp-row--selected]="isSelected(hit)"
                      (click)="pick(hit, $event)">
                <span class="sp-row__gl">◆</span>
                <span class="sp-row__main">
                  <span class="sp-row__nm">{{ hit.name || identifierLabel(hit) }}</span>
                  <span class="sp-row__meta">{{ fullMeta(hit) }}</span>
                </span>
                <span class="sp-tag sp-tag--kept" *ngIf="hit.kept">KEPT</span>
              </button>
            </div>
          </ng-container>

          <div class="sp-error" *ngIf="error">{{ error }}</div>
        </div>
        </app-toolbar-drawer>
      </ng-container>
    </ng-template>
  `,
  styles: [`
    .sp-panel { width: 332px; max-width: 100%; }

    .sp-box {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 5px 8px;
      margin-bottom: 8px;
      border: 1px solid rgba(128, 128, 128, 0.4);
      border-radius: 8px;
    }
    .sp-box:focus-within { border-color: #2563EB; }
    .sp-box__mag { flex: 0 0 auto; font-size: 16px; width: 16px; height: 16px; line-height: 16px; opacity: 0.6; }
    .sp-box__input { flex: 1 1 auto; min-width: 0; font: inherit; font-size: 12.5px; border: none; background: transparent; color: inherit; outline: none; }
    .sp-box__clear { flex: 0 0 auto; display: inline-flex; border: none; background: transparent; color: inherit; opacity: 0.55; cursor: pointer; padding: 0; }
    .sp-box__clear:hover { opacity: 1; }
    .sp-box__clear .mat-icon { font-size: 15px; width: 15px; height: 15px; line-height: 15px; }

    .sp-actions {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 8px;
    }

    .sp-hint {
      font-size: 11px;
      opacity: 0.72;
      line-height: 1.35;
      margin: -4px 0 8px 2px;
    }

    .sp-quiet { font-size: 11.5px; opacity: 0.7; line-height: 1.45; padding: 4px 2px; }

    .sp-list { display: flex; flex-direction: column; max-height: 208px; overflow-y: auto; }

    .sp-row {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 6px 4px;
      font: inherit;
      text-align: left;
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      border-radius: 6px;
    }
    .sp-row + .sp-row { border-top: 1px solid rgba(128, 128, 128, 0.18); }
    .sp-row:hover { background: rgba(37, 99, 235, 0.08); }
    .sp-row:focus-visible { outline: 2px solid #2563EB; outline-offset: -2px; }
    .sp-row--selected {
      background: rgba(37, 99, 235, 0.16) !important;
      border: 1px solid rgba(37, 99, 235, 0.5) !important;
    }
    .sp-row--selected .sp-row__nm {
      color: #2563EB;
      font-weight: 700;
    }
    .sp-row--selected .sp-row__gl {
      color: #2563EB;
      opacity: 1;
    }
    :host-context(.dark) .sp-row--selected,
    :host-context(.dark-theme) .sp-row--selected {
      background: rgba(63, 154, 255, 0.22) !important;
      border-color: rgba(96, 165, 250, 0.6) !important;
    }
    :host-context(.dark) .sp-row--selected .sp-row__nm,
    :host-context(.dark-theme) .sp-row--selected .sp-row__nm {
      color: #60a5fa !important;
    }
    :host-context(.dark) .sp-row--selected .sp-row__gl,
    :host-context(.dark-theme) .sp-row--selected .sp-row__gl {
      color: #60a5fa !important;
    }
    .sp-row__gl { flex: 0 0 auto; font-size: 10px; opacity: 0.55; width: 10px; }
    .sp-row__main { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; line-height: 1.25; }
    .sp-row__nm { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .sp-row__meta { font-size: 10.5px; opacity: 0.65; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .sp-tag { flex: 0 0 auto; font-size: 9px; letter-spacing: 0.04em; padding: 1px 5px; border-radius: 999px; border: 1px solid rgba(128, 128, 128, 0.4); opacity: 0.75; }
    .sp-tag--kept { border-color: rgba(22, 163, 74, 0.5); color: #15803d; }

    .sp-error { margin-top: 8px; padding: 7px 8px; border-radius: 6px; background: rgba(217, 119, 6, 0.12); font-size: 11px; line-height: 1.45; }

    /*
     * Same treatment as the Port Calls and Expected Arrivals CTAs: these
     * buttons carry no cost badge, so the drawer's shared rule for
     * .tb-drawer__primary-label (flex: 1; text-align: left) would strand the
     * label hard left against a lone icon. Icon and label centre as a pair.
     *
     * Doubled on .tb-drawer__primary for the same specificity reason spelled
     * out in those two files. NOTE: no backticks in here — this comment lives
     * inside the styles template literal, and one would close the string.
     */
    .tb-drawer__primary.vf-cta { justify-content: center; }
    .tb-drawer__primary.vf-cta .tb-drawer__primary-label { flex: 0 0 auto; text-align: center; }

    .sp-spin { animation: sp-spin 1.1s linear infinite; }
    @keyframes sp-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .sp-spin { animation: none; } }
  `],
})
export class VfSearchControlComponent extends BaseControl {
  @ViewChild('panel') panel!: TemplateRef<any>;
  @ViewChild('trigger') triggerRef!: ElementRef;

  raw = '';
  useSatellite = false;
  loading = false;
  error: string | null = null;
  results: VesselFinderVesselRow[] = [];
  selectedKey: string | null = null;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    private readonly api: ApiService,
    private readonly popupService: PopupService,
    public vfCtrl: VesselFinderMapController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  private get sensor(): any {
    return this.context.options?.['sensor'];
  }

  get satelliteCost(): number {
    return actionCredits(this.sensor, 'searchSatellite');
  }
  get terrestrialCost(): number {
    return actionCredits(this.sensor, 'search');
  }

  subtitle(): string {
    const t = `${this.terrestrialCost} ${this.terrestrialCost === 1 ? 'credit' : 'credits'} terrestrial`;
    const s = `${this.satelliteCost} ${this.satelliteCost === 1 ? 'credit' : 'credits'} satellite`;
    return `${t} · ${s}`;
  }

  /**
   * Null while the spend may proceed, a human-readable refusal otherwise.
   *
   * Two of them, because this drawer has two prices: a wallet that cannot
   * afford a satellite fix can still afford a terrestrial one, and blocking
   * both on the dearer of the two would withhold a search the user can pay
   * for. The line under the buttons reports the TERRESTRIAL one — if even the
   * cheaper action is unaffordable, nothing in this drawer is.
   */
  terrestrialBlocker(): string | null {
    return vesselFinderSpendBlocker(this.sensor, searchActionKey(false));
  }
  satelliteBlocker(): string | null {
    return vesselFinderSpendBlocker(this.sensor, searchActionKey(true));
  }

  costLabel(): string {
    const c = this.useSatellite ? this.satelliteCost : this.terrestrialCost;
    return `${c} ${c === 1 ? 'credit' : 'credits'}/vessel`;
  }

  searchLabel(): string {
    return this.useSatellite ? 'Satellite Search' : 'Terrestrial Search';
  }

  identifierLabel(r: VesselFinderVesselRow): string {
    return r.mmsi ? `MMSI ${r.mmsi}` : r.imo ? `IMO ${r.imo}` : '—';
  }

  fullMeta(r: VesselFinderVesselRow): string {
    const parts: string[] = [];
    if (r.mmsi) parts.push(`MMSI ${r.mmsi}`);
    if (r.imo) parts.push(`IMO ${r.imo}`);
    const ts = this.formatTimestamp(r.timestamp ?? r.timeUtc);
    if (ts) parts.push(ts);
    // A hull with no broadcast name is already TITLED with its identifier
    // label, so repeating that same label here printed the row twice over —
    // "MMSI 636024674" above "MMSI 636024674". Only the duplicate goes: a
    // second identifier or a timestamp still belongs on this line.
    const rest = r.name ? parts : parts.filter((p) => p !== this.identifierLabel(r));
    return rest.length ? rest.join(' · ') : '—';
  }

  private formatTimestamp(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getUTCDate())} ${months[d.getUTCMonth()]} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
  }

  /**
   * The free "already on this map" list.
   *
   * Positionless rows are filtered OUT, because the only thing clicking a row
   * here does is `vf.centerVessel` — and a hull with no fix draws no marker,
   * so `centerVessel` found nothing and the click did nothing at all. Such
   * rows are real: the Port calls drawer's own MMSI box upserts a bare
   * `{mmsi, name: null}` row on a successful fetch, which then surfaced here
   * on the next search looking exactly like a stale cached result.
   */
  localHits(s: VesselFinderState): VesselFinderVesselRow[] {
    const q = this.raw.trim().toLowerCase();
    if (!q || this.results.length) return [];
    const list = s.locatedVessels ?? [];
    return list.filter(
      (r) =>
        vesselRowHasFix(r) &&
        ((r.name ?? '').toLowerCase().includes(q) ||
          String(r.mmsi ?? '').includes(q) ||
          String(r.imo ?? '').includes(q)),
    );
  }

  isKept(r: VesselFinderVesselRow, s: VesselFinderState): boolean {
    if (r.kept) return true;
    const key = r.mmsi ?? r.imo;
    if (!key) return false;
    return (s.locatedVessels ?? []).some((v) => (v.mmsi ?? v.imo) === key && v.kept);
  }

  onQueryChange(): void {
    this.error = null;
    if (this.raw.trim() && this.results.length) {
      this.results = [];
    }
  }

  /**
   * The drawer renders THIS component's own `TemplateRef`, so closing it
   * destroys the view but never the component — `results` survived into the
   * next open, and since the list renders on `results.length` alone (not on
   * `raw`), the drawer reopened showing the previous search's hulls under an
   * empty search box. Reset the whole search, not just the text.
   */
  onToggleDrawer(trigger: any): void {
    this.clear();
    this.drawer.toggle(this.panel, this.vcr, trigger);
  }

  onCloseDrawer(): void {
    this.clear();
    this.drawer.close();
  }

  isSelected(r: VesselFinderVesselRow): boolean {
    const key = String(r.mmsi ?? r.imo ?? '');
    return !!key && key === this.selectedKey;
  }

  clear(): void {
    this.raw = '';
    this.results = [];
    this.selectedKey = null;
    this.error = null;
  }

  pick(r: VesselFinderVesselRow, event?: Event): void {
    event?.stopPropagation();
    const key = String(r.mmsi ?? r.imo ?? '');
    if (key) {
      this.selectedKey = key;
      this.vfCtrl.upsertLocatedVessel({ ...r, stale: false });
      this.vfCtrl.centerVessel(key);
    }
  }

  searchTerrestrial(): void {
    this.useSatellite = false;
    this.executeSearch(false);
  }

  searchSatellite(): void {
    this.useSatellite = true;
    this.executeSearch(true);
  }

  search(): void {
    if (this.useSatellite) {
      this.searchSatellite();
    } else {
      this.searchTerrestrial();
    }
  }

  private executeSearch(isSatellite: boolean): void {
    this.error = null;
    const cap = Number(this.sensor?.vesselFinderIdentifierCap) || 5;
    const parsed = parseVesselIdentifiers(this.raw, cap);

    if (parsed.rejected.length) {
      this.error = `Not a valid MMSI/IMO: ${parsed.rejected.join(', ')}`;
      return;
    }
    if (!parsed.mmsi.length && !parsed.imo.length) {
      this.error = 'Enter at least one MMSI or IMO.';
      return;
    }
    if (parsed.overCap) {
      this.error = `Too many identifiers — the limit is ${cap} per search.`;
      return;
    }

    const actionKey = searchActionKey(isSatellite);
    const refusal = vesselFinderSpendRefusal(this.sensor, actionKey);
    if (refusal) {
      this.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return;
    }

    const { taskid, sensorId } = this.vfCtrl;
    if (!taskid || !sensorId) {
      this.error = 'This sensor is not attached to a task yet.';
      return;
    }

    this.loading = true;
    this.api
      .searchVesselFinderVessels({
        taskid,
        sensorId,
        mmsi: parsed.mmsi,
        imo: parsed.imo,
        satellite: isSatellite,
      })
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          this.raw = '';
          applyVesselFinderCreditSpend(
            this.sensor,
            res,
            actionCredits(this.sensor, actionKey),
          );
          const data = res?.data;
          if (!data || !Array.isArray(data) || !data.length) {
            this.results = [];
            this.vfCtrl.setSearchResults([]);
            this.error = 'No data found.';
            return;
          }
          this.results = data.map((v: any) => ({
            mmsi: v?.mmsi ?? null,
            imo: v?.imo ?? null,
            name: v?.name ?? null,
            timeUtc: v?.timestamp ?? null,
            timestamp: v?.timestamp ?? null,
            stale: false,
            kept: false,
            latitude: v?.latitude ?? null,
            longitude: v?.longitude ?? null,
            speed: v?.speed ?? null,
            course: v?.course ?? null,
            heading: v?.heading ?? null,
            destination: v?.destination ?? null,
            eta: v?.eta ?? null,
            positionSource: v?.positionSource ?? null,
            callSign: v?.callSign ?? null,
            shipType: v?.shipType ?? null,
            typeCode: v?.typeCode ?? null,
            flag: v?.flag ?? null,
          }));
          this.vfCtrl.setSearchResults(this.results);
        },
        error: (err) => {
          this.loading = false;
          this.error = err?.error?.msg || 'Search failed. Please try again.';
        },
      });
  }
}
